import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { employeeManualOrderApi } from '@/api/employee-manual-order'
import type {
  ConfirmManualOrderPaymentParams,
  EmployeeManualOrderImportParams,
  EmployeeManualOrderListParams,
  ManualOrderDeliveryParams,
  ManualOrderApplyRefundParams,
  ManualOrderMarkRefundReturnedParams,
} from '@/api/employee-manual-order'

export const employeeManualOrderKeys = {
  all: ['employee', 'manual-orders'] as const,
  lists: () => [...employeeManualOrderKeys.all, 'list'] as const,
  list: (params: EmployeeManualOrderListParams) => [...employeeManualOrderKeys.lists(), params] as const,
  details: () => [...employeeManualOrderKeys.all, 'detail'] as const,
  detail: (id: number) => [...employeeManualOrderKeys.details(), id] as const,
  paymentQrcode: (id: number) => [...employeeManualOrderKeys.all, 'payment-qrcode', id] as const,
}

/** 获取员工手动订单列表 */
export function useEmployeeManualOrders(params: EmployeeManualOrderListParams) {
  return useQuery({
    queryKey: employeeManualOrderKeys.list(params),
    queryFn: () => employeeManualOrderApi.list(params),
    placeholderData: (previousData) => previousData,
  })
}

/** 获取员工手动订单详情 */
export function useEmployeeManualOrder(id: number, enabled = true, scope?: string) {
  return useQuery({
    queryKey: [...employeeManualOrderKeys.detail(id), scope],
    queryFn: () => employeeManualOrderApi.detail(id, scope),
    enabled: enabled && id > 0,
  })
}

/** 获取员工手动订单收款码 */
export function useEmployeeManualOrderPaymentQrcode(id: number, enabled = true) {
  return useQuery({
    queryKey: employeeManualOrderKeys.paymentQrcode(id),
    queryFn: () => employeeManualOrderApi.paymentQrcode(id),
    enabled: enabled && id > 0,
  })
}

/** 确认员工手动订单线下收款 */
export function useConfirmEmployeeManualOrderPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data?: ConfirmManualOrderPaymentParams }) =>
      employeeManualOrderApi.confirmPayment(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
      queryClient.invalidateQueries({
        queryKey: employeeManualOrderKeys.paymentQrcode(variables.id),
      })
      toast.success('确认收款成功')
    },
  })
}

/** 申请员工手动订单退货 */
export function useApplyEmployeeManualOrderRefund() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data, scope }: { id: number; data: ManualOrderApplyRefundParams; scope?: string }) =>
      employeeManualOrderApi.applyRefund(id, data, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
      toast.success('申请退货已提交')
    },
  })
}

/** 取消员工手动订单 */
export function useCancelEmployeeManualOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: number }) => employeeManualOrderApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
      toast.success('订单已取消')
    },
  })
}

/** 删除员工手动订单 */
export function useDeleteEmployeeManualOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: number }) => employeeManualOrderApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
      toast.success('订单已删除')
    },
  })
}

/** 批量删除员工手动订单 */
export function useBatchDeleteEmployeeManualOrders() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ids }: { ids: number[] }) => employeeManualOrderApi.batchDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
      toast.success('批量删除成功')
    },
  })
}

/** 员工手动订单发货 */
export function useDeliveryEmployeeManualOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ManualOrderDeliveryParams }) =>
      employeeManualOrderApi.delivery(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
      toast.success('发货成功')
    },
  })
}

/** 导入员工手动订单发货 Excel */
export function useImportEmployeeManualOrderDeliveryExcel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ file, params }: { file: File; params?: EmployeeManualOrderImportParams }) =>
      employeeManualOrderApi.importDeliveryExcel(file, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
    },
  })
}

/** 预览员工手动订单发货清单 */
export function usePreviewEmployeeManualOrderShippingLists() {
  return useMutation({
    mutationFn: ({ ids }: { ids: number[] }) => employeeManualOrderApi.previewShippingLists(ids),
  })
}

/** 录入人员代用户登记退货（refund_type 4→5） */
export function useMarkManualOrderReturned() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data, scope }: { id: number; data: ManualOrderMarkRefundReturnedParams; scope?: string }) =>
      employeeManualOrderApi.markRefundReturned(id, data, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
      toast.success('退货信息已提交')
    },
  })
}
